/**
 * Map filters based on stored device configuration (configuration.ap /
 * configuration.treemgmt), not live state reports (private_wifi, ul_band, etc.).
 */

import { isCapDevice } from './publicWifiRrmPlanUi';

function bandFromRadioName(name) {
  const lower = String(name || '').trim().toLowerCase();
  if (lower.startsWith('2.4ghz')) return '2.4GHz';
  if (lower.startsWith('5ghz')) return '5GHz';
  if (lower.startsWith('6ghz')) return '6GHz';
  return '';
}

export function getDeviceConfiguration(device) {
  const cfg = device?.configuration;
  return cfg && typeof cfg === 'object' ? cfg : null;
}

/** True when configuration.ap has an entry targeting a 5 GHz radio slot. */
export function deviceHas5GHzApInConfig(device) {
  const cfg = getDeviceConfiguration(device);
  if (!cfg) return false;
  const apRaw = cfg.ap;
  if (!Array.isArray(apRaw)) return false;
  return apRaw.some((ap) => {
    if (!ap || typeof ap !== 'object') return false;
    return bandFromRadioName(ap.radio) === '5GHz';
  });
}

function parseTreemgmtMaxChildren(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const raw = entry.max_children;
  if (raw === undefined || raw === null || raw === '') return null;
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * True when at least one treemgmt entry allows mesh children
 * (max_children unset or non-zero).
 */
export function deviceAllowsChildrenInConfig(device) {
  const cfg = getDeviceConfiguration(device);
  const treemgmt = cfg?.treemgmt;
  if (!Array.isArray(treemgmt) || treemgmt.length === 0) return false;
  return treemgmt.some((entry) => {
    const maxChildren = parseTreemgmtMaxChildren(entry);
    return maxChildren === null || maxChildren !== 0;
  });
}

/**
 * True when at least one treemgmt entry explicitly blocks mesh children
 * (max_children === 0).
 */
export function deviceBlocksChildrenInConfig(device) {
  const cfg = getDeviceConfiguration(device);
  const treemgmt = cfg?.treemgmt;
  if (!Array.isArray(treemgmt) || treemgmt.length === 0) return false;
  return treemgmt.some((entry) => parseTreemgmtMaxChildren(entry) === 0);
}

export function devicePassesFiveGApConfigFilter(device, filter) {
  if (!filter || filter === 'all') return true;
  const hasAp = deviceHas5GHzApInConfig(device);
  if (filter === 'yes') return hasAp || isCapDevice(device);
  if (filter === 'no') return !hasAp;
  return true;
}

export function devicePassesChildrenAllowedConfigFilter(device, filter) {
  if (!filter || filter === 'all') return true;
  if (filter === 'yes') return deviceAllowsChildrenInConfig(device);
  if (filter === 'no') return deviceBlocksChildrenInConfig(device);
  return true;
}

/**
 * True when the device's 5 GHz radio slot is used for Public WiFi:
 * configuration.ap[] has an entry on a 5 GHz radio (e.g. 5GHz_0) AND
 * the corresponding treemgmt entry has max_children = 0 (mesh blocked —
 * which is what SSID assign does when it takes over the slot).
 *
 * Backhaul-only changes (tx_power_down, band, htmode) must NOT be
 * applied to these devices because changing the AP radio config would
 * affect the Public WiFi service, not the backhaul mesh.
 */
export function device5GHzRadioUsedForPublicWifi(device) {
  const cfg = getDeviceConfiguration(device);
  if (!cfg) return false;

  const apRaw = cfg.ap;
  if (!Array.isArray(apRaw)) return false;

  const fiveGRadioSlots = new Set();
  for (const ap of apRaw) {
    if (!ap || typeof ap !== 'object') continue;
    const radio = String(ap.radio || '').trim();
    if (bandFromRadioName(radio) === '5GHz') {
      fiveGRadioSlots.add(radio.toLowerCase());
    }
  }
  if (fiveGRadioSlots.size === 0) return false;

  const treemgmt = cfg.treemgmt;
  if (!Array.isArray(treemgmt)) return false;

  return treemgmt.some((entry) => {
    if (!entry || typeof entry !== 'object') return false;
    const treeId = String(entry.tree_id || '').trim().toLowerCase();
    if (!fiveGRadioSlots.has(treeId)) return false;
    const maxChildren = parseTreemgmtMaxChildren(entry);
    return maxChildren === 0;
  });
}

/** Lowercased serial set for a custom-tree / segment roster. */
export function segmentMemberSerials(tree) {
  const set = new Set();
  (tree?.devices || []).forEach((s) => {
    if (s) set.add(String(s).toLowerCase());
  });
  (tree?.caps || []).forEach((s) => {
    if (s) set.add(String(s).toLowerCase());
  });
  return set;
}

export function deviceBelongsToSegmentTree(device, tree) {
  const serial = device?.serial_number?.toLowerCase();
  if (!serial || !tree) return false;
  return segmentMemberSerials(tree).has(serial);
}

/** Custom-tree segment roster serials (lowercase). */
export function allSegmentRegistrySerials(customTrees) {
  const set = new Set();
  (customTrees || []).forEach((tree) => {
    segmentMemberSerials(tree).forEach((s) => set.add(s));
  });
  return set;
}

/** Sentinel id for the map overlay "No segment" highlight entry. */
export const NO_SEGMENT_OVERLAY_ID = '__no_segment__';

/** Serials of site devices not listed in any custom-tree segment roster. */
export function collectUnassignedSegmentSerials(devices, customTrees) {
  const registry = allSegmentRegistrySerials(customTrees);
  const out = new Set();
  (devices || []).forEach((device) => {
    const serial = device?.serial_number?.toLowerCase();
    if (serial && !registry.has(serial)) {
      out.add(serial);
    }
  });
  return out;
}

/** Map filter: all | one or more custom-tree ids | NO_SEGMENT_OVERLAY_ID (unassigned). */
export function normalizeSegmentFilterIds(segmentFilterIds) {
  if (segmentFilterIds == null || segmentFilterIds === '' || segmentFilterIds === 'all') return [];
  if (Array.isArray(segmentFilterIds)) {
    return segmentFilterIds.map((id) => String(id).trim()).filter(Boolean);
  }
  return String(segmentFilterIds).split(',').map((s) => s.trim()).filter(Boolean);
}

function devicePassesOneSegmentFilter(device, segmentFilterId, customTrees) {
  const trees = Array.isArray(customTrees) ? customTrees : [];
  if (segmentFilterId === NO_SEGMENT_OVERLAY_ID) {
    const registry = allSegmentRegistrySerials(trees);
    const serial = device?.serial_number?.toLowerCase();
    return Boolean(serial && !registry.has(serial));
  }
  const tree = trees.find((t) => t?.id === segmentFilterId);
  if (!tree) return false;
  return deviceBelongsToSegmentTree(device, tree);
}

/** Passes when the device belongs to any selected segment (OR). Empty selection = all. */
export function devicePassesSegmentFilter(device, segmentFilterIds, customTrees) {
  const ids = normalizeSegmentFilterIds(segmentFilterIds);
  if (ids.length === 0) return true;
  return ids.some((id) => devicePassesOneSegmentFilter(device, id, customTrees));
}

function parseConnectedTimeSeconds(raw) {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed || trimmed.toLowerCase() === 'n/a') return null;
    const n = parseFloat(trimmed);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function deviceAssociationMatchKeys(device) {
  const keys = new Set();
  (device?.radios || []).forEach((radio) => {
    const mac = radio?.mac?.toLowerCase();
    if (mac) keys.add(mac);
  });
  const serial = device?.serial_number?.toLowerCase();
  if (serial) {
    keys.add(serial);
    keys.add(serial.replace(/:/g, ''));
  }
  return keys;
}

/**
 * Connected time (seconds) from the parent device's WiFi association entry
 * for this node — same source as the device details page.
 * Returns null when unavailable (no parent, no association, invalid value).
 */
export function getDeviceConnectedTimeSec(device, allDevices) {
  if (!device?.parent_serial) return null;
  const parentSerial = String(device.parent_serial).toLowerCase();
  const parent = (allDevices || []).find(
    (d) => d?.serial_number && String(d.serial_number).toLowerCase() === parentSerial,
  );
  if (!parent) return null;

  const parentIfaces = parent.private_wifi?.interfaces || parent.private_wifi?.Interfaces;
  if (!Array.isArray(parentIfaces) || parentIfaces.length === 0) return null;

  const identifiers = deviceAssociationMatchKeys(device);
  if (identifiers.size === 0) return null;

  for (const iface of parentIfaces) {
    const associations = iface?.Associations || iface?.associations;
    if (!Array.isArray(associations)) continue;
    for (const assoc of associations) {
      const station = assoc?.Station || assoc?.station;
      if (!station) continue;
      const stationId = String(station).toLowerCase().replace(/:/g, '');
      const stationLower = String(station).toLowerCase();
      if (!identifiers.has(stationId) && !identifiers.has(stationLower)) continue;
      const sec = parseConnectedTimeSeconds(assoc?.ConnectedTime ?? assoc?.connected_time);
      if (sec != null) return sec;
    }
  }
  return null;
}

/**
 * Map filter: connected time vs threshold (minutes). compare: all | gt | lt.
 * A device with no resolvable connected time (no parent, no association, or an
 * invalid/missing value) is treated as 0 seconds connected — so it is excluded
 * from "more than" results and included in "less than" results.
 */
export function devicePassesConnectedTimeFilter(device, compare, minutesRaw, allDevices) {
  if (!compare || compare === 'all') return true;
  const minutes = parseFloat(String(minutesRaw ?? '').trim());
  if (!Number.isFinite(minutes) || minutes < 0) return true;
  const thresholdSec = minutes * 60;
  const connectedSec = getDeviceConnectedTimeSec(device, allDevices) ?? 0;
  if (compare === 'gt') return connectedSec > thresholdSec;
  if (compare === 'lt') return connectedSec < thresholdSec;
  return true;
}
